import { getToken, refreshToken, clearSession } from './auth'
import type { PlatformModel } from '@/types'

export interface EngReviewRec {
  id: string
  kar_id: string
  run_id: string
  run_type: string
  category: string
  title: string
  priority: 'high' | 'medium' | 'low'
  status: 'open' | 'accepted' | 'accepted_pending_execution' | 'declined' | 'more_research' | 'snoozed' | 'completed'
  reasoning: string
  evidence: string
  recommended_action: string
  is_direct_question: number
  created_at: string
  updated_at: string
  actioned_at: string | null
  actioned_by: string | null
  snooze_until: string | null
  execution_plan?: string | null
}

const BASE_URL = '/api'

class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = getToken()
  const url = `${BASE_URL}${path}`

  const res = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options?.headers,
    },
  })

  if (res.status === 401) {
    const refreshed = await refreshToken()
    if (refreshed) {
      const retry = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${refreshed}`,
          ...options?.headers,
        },
      })
      if (retry.ok) return retry.json()
    }
    clearSession()
    throw new ApiError(401, 'Unauthorized - please log in')
  }

  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText)
    throw new ApiError(res.status, text)
  }

  return res.json()
}

export { ApiError }

export interface MemoryTreeNode {
  name: string
  type: 'bucket' | 'folder' | 'file'
  key?: string
  size?: number
  uploaded?: string
  children?: MemoryTreeNode[]
}

export interface PipelineItem {
  id: string
  idea_number: number | null
  idea_title: string
  status: 'awaiting_review' | 'in_progress' | 'paused' | 'completed' | 'failed' | 'rejected'
  current_stage: 'idea' | 'planning' | 'design' | 'build' | 'approval'
  created_at: number
  updated_at: number
  previous_rejections?: number
}

export interface PipelineConversation {
  id: string
  stage_output_id: string
  role: string
  content: string
  created_at: number
}

export interface PipelineStageOutput {
  id: string
  pipeline_item_id: string
  stage: string
  status: 'awaiting_review' | 'approved' | 'rejected' | 'running' | 'failed'
  engine: string
  input_summary: string
  output: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  output_parsed: Record<string, any> | null
  started_at: number
  completed_at: number | null
  reviewed_at: number | null
  created_at: number
  conversations: PipelineConversation[]
}

export interface CapabilityView {
  principal_id: string
  enforcement_mode: string
  execution_authorized: false
  super_admin: boolean
  can_administer: boolean
  capabilities: Array<{
    id: string; domain: string; display_name: string; description: string
    effect: 'allow' | 'deny'; reason: string
    state: 'default' | 'custom' | 'restricted' | 'unavailable'
    evidence: Array<{ decisive: boolean; group_name?: string; source_type: string }>
  }>
}


export interface CapabilityAdministration {
 management_scopes:Array<{id:string;manager_id:string;target_id:string;capability_id:string;effective_from:string|null;expires_at:string|null}>
 organisations:Array<{id:string;display_name:string}>
 can_manage_groups:boolean
 locked_capabilities:string[]
 assignments: Array<{id:string;group_id:string;status:string;effective_from:string;expires_at:string|null}>
 groups: Array<{id:string;name:string;kind:string;group_type:string;read_only:number;active:number}>
 policies: Array<{group_id:string;capability_id:string;granted:number}>
 definitions: Array<{id:string;domain:string;display_name:string;action_class:string}>
 overrides: Array<{id:string;capability_id:string;override_type:string;effective_from:string;expires_at:string|null}>
 principal_id:string|null
 can_mutate:boolean
}
export interface CapabilityPolicyChange {
 request_id:string;operation:'principal_override'|'group_capability';target_id:string;
 capability_id:string;desired:'grant'|'restrict'|'inherit';reason:string;
 expected_override_id?:string|null;expected_group_granted?:number|null;
}

export type CapabilityGroupChange = {request_id:string;target_id:string;reason:string} & (
 {operation:'clone_group';source_group_id:string;name:string} |
 {operation:'assign_group'|'revoke_assignment';group_id:string;expected_assignment_id:string|null})
export type ManagementScopeChange = {request_id:string;operation:'grant_management_scope'|'revoke_management_scope';target_id:string;manager_id:string;capability_id:string;expected_scope_id:string|null;reason:string}
export type CapabilityChange = CapabilityPolicyChange | CapabilityGroupChange | ManagementScopeChange

export interface ExtensionActivityEvent {
 event_id:string;device_id:string;browser_session_id:string;
 recorded_at:string;occurred_at:string;source:string;action:string;
 phase:string;outcome:string;reason_code:string|null;
 request_id:string|null;correlation_id:string|null;workflow_id:string|null;
 step_index:number|null;duration_ms:number|null;frame_age_ms:number|null;
 geometry_available:number|null;details_json:string
}
export type ExtensionActivityFilters = Partial<Record<
 'device_id'|'browser_session_id'|'request_id'|'correlation_id'|
 'workflow_id'|'action'|'phase'|'outcome'|'source'|'before'|'before_event_id',string>>
 & {limit?:number}

export const api = {
  capabilitySubjects: () => apiFetch<{subjects:Array<{principal_id:string;display_name?:string}>;can_manage_groups:boolean}>('/dashboard/capability-control/subjects'),
  dashboardNavigation: () => apiFetch<{principal_id:string;sections:string[];super_admin:boolean}>('/dashboard/capability-control/navigation'),
  capabilityAdministration: (principalId?:string) => apiFetch<CapabilityAdministration>('/dashboard/capability-control/administration'+(principalId?'?principal_id='+encodeURIComponent(principalId):'')),
  changeCapabilityPolicy: (change:CapabilityChange,approval_token?:string) => apiFetch<{success?:boolean;pending?:boolean;approval_token?:string}>('/dashboard/capability-control/policy',{method:'POST',body:JSON.stringify({change,...(approval_token?{approval_token}:{})})}),

  capabilityView: (principalId?: string) => apiFetch<CapabilityView>(`/dashboard/capability-control/effective${principalId === undefined ? '' : '?principal_id=' + encodeURIComponent(principalId)}`),
  // Gateway session + tenant switching
  gatewaySession: () => apiFetch<{
    user_id: string; email: string; display_name: string;
    plan: string; tenant_id: string | null; tenant_role: string | null; permissions: string[];
  }>('/dashboard/session/identity'),

  gatewayTenants: () => apiFetch<{
    count: number;
    tenants: Array<{ tenant_id: string; name: string; slug: string; plan: string; status: string; role: string }>;
  }>('/tenants'),

  switchTenant: (tenantId: string) => apiFetch<{
    token: string; user_id: string; tenant_id: string; role: string; plan: string; permissions: string[];
  }>('/tenants/switch', {
    method: 'POST',
    body: JSON.stringify({ tenant_id: tenantId }),
  }),

  // Team management (gateway /tenant/team)
  team: () => apiFetch<{
    members: Array<{ user_id: string; role: string; joined_at: string; email: string; display_name: string }>;
    seat_count: number; seat_limit: number | null; seats_remaining: number | null; plan: string;
  }>('/tenant/team'),

  teamInvite: (email: string, role: string) => apiFetch<{
    tenant_id: string; user_id: string; role: string; message: string;
  }>('/tenant/team', {
    method: 'POST',
    body: JSON.stringify({ email, role }),
  }),

  teamUpdateRole: (userId: string, role: string) => apiFetch<{
    user_id: string; role: string; message: string;
  }>(`/tenant/team/${encodeURIComponent(userId)}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ role }),
  }),

  teamRemove: (userId: string) => apiFetch<{
    user_id: string; message: string;
  }>(`/tenant/team/${encodeURIComponent(userId)}`, { method: 'DELETE' }),

  // Deployment approvals
  deploymentApprovals: () => apiFetch<{
    approvals: Array<{
      approval_id: string; requester_id: string; environment: string; worker_name: string;
      status: string; created_at: string; expires_at: string;
      requester_email: string; requester_name: string;
    }>;
    count: number;
  }>('/tenant/approvals'),

  approveDeployment: (approvalId: string) => apiFetch<{
    approval_id: string; status: string; worker_name: string; environment: string; message: string;
  }>(`/tenant/approvals/${encodeURIComponent(approvalId)}/approve`, { method: 'POST' }),

  rejectDeployment: (approvalId: string, reason?: string) => apiFetch<{
    approval_id: string; status: string; reason: string | null; message: string;
  }>(`/tenant/approvals/${encodeURIComponent(approvalId)}/reject`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason ?? null }),
  }),

  initiateDeploy: (params: { environment: string; worker_name: string; deploy_config?: unknown }) =>
    apiFetch<{
      status: string; environment: string; worker_name: string; message: string;
      approval_id?: string; expires_at?: string;
    }>('/tenant/deploy', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  // Slack
  slackConfig: () => apiFetch<{
    connected: boolean; channel_name: string | null;
    notify_deploy: boolean; notify_approval: boolean; notify_security: boolean;
  }>('/tenant/slack'),

  setSlackWebhook: (params: { webhook_url: string; channel_name?: string; notify_deploy?: boolean; notify_approval?: boolean }) =>
    apiFetch<{ connected: boolean; channel_name: string | null; message: string }>('/tenant/slack', {
      method: 'POST',
      body: JSON.stringify(params),
    }),

  disconnectSlack: () => apiFetch<{ connected: boolean; message: string }>('/tenant/slack', { method: 'DELETE' }),

  // Billing
  billingStatus: () => apiFetch<{
    subscription: Record<string, unknown> | null;
    founding_member_window: Record<string, unknown> | null;
    plans: Array<Record<string, unknown>>;
  }>('/billing/status'),

  billingCheckout: (planId: string, billingCycle: string) => apiFetch<{
    session_id: string; checkout_url: string; is_founding_member: boolean; fm_spots_remaining: number;
  }>('/billing/checkout', {
    method: 'POST',
    body: JSON.stringify({ plan_id: planId, billing_cycle: billingCycle }),
  }),

  billingPortal: () => apiFetch<{ portal_url: string }>('/billing/portal', { method: 'POST' }),

  // GitHub
  githubStatus: () => apiFetch<{
    connected: boolean; connection_type: 'app' | 'pat' | null;
    account_login: string | null; account_type: string | null;
    account_avatar_url: string | null; repos_access: string | null;
    pat_hint?: string; github_host?: string;
  }>('/auth/github/status'),

  // WordPress (ops-wordpress-connection, the current, live connection)
  wordpressDeepStatus: () => apiFetch<{
    connected?: boolean; has_credential?: boolean; status?: string;
    site_url?: string; mcp_token_configured?: boolean;
    title?: string; active_theme?: string; active_plugins_count?: number;
    error?: string;
  }>('/dashboard/wordpress-deep/status'),

  // Real CPU/memory/disk/network from the VPS itself, via exec-bridge
  vpsResourceUsage: () => apiFetch<{
    success: boolean
    cpu: { percent: number | null }
    memory: { percent: number | null; used_mb: number | null; total_mb: number | null }
    disk: { percent: number | null; used: string | null; total: string | null }
    network: { rx_gb: number; tx_gb: number; note: string }
  }>('/dashboard/vps-resource-usage'),

  // Real deploy counts per day, from the events table (event_type='deploy')
  deploymentsByDay: () => apiFetch<{
    success: boolean
    period: string
    by_day: Array<{ day: string; deployments: number }>
  }>('/dashboard/deployments-by-day'),

  githubInitiate: () => apiFetch<{ auth_url: string }>('/auth/github'),

  githubDisconnect: () => apiFetch<{ connected: boolean; message: string }>('/auth/github', { method: 'DELETE' }),

  githubRepos: () => apiFetch<{
    repos: Array<{ name: string; full_name: string; private: boolean; default_branch: string; updated_at: string }>;
    connection_type: string;
  }>('/auth/github/repos'),

  githubConnectPat: (pat: string, githubHost?: string) => apiFetch<{
    success: boolean; username: string; hint: string;
  }>('/auth/github/pat', {
    method: 'POST',
    body: JSON.stringify({ pat, github_host: githubHost }),
  }),

  // MCP Catalogue
  mcpCatalogueRefresh: () => apiFetch<{
    success: boolean
    tool_count: number
    generated_at: string
    message: string
  }>('/dashboard/mcp-catalogue', { method: 'POST' }),

  mcpToolsRefresh: () => apiFetch<{
    success: boolean
    tool_count: number
    sessions_notified: number
    active_connections: number
    generated_at: string
    message: string
  }>('/dashboard/mcp-tools-refresh', { method: 'POST' }),

  mcpCatalogueGet: () => apiFetch<{
    tool_count: number
    generated_at: string | null
    message?: string
    tools?: Array<{ name: string; description: string; tier: number }>
  }>('/dashboard/mcp-catalogue'),

  // Existing dashboard endpoints below
  health: () => apiFetch<{
    status: 'healthy' | 'degraded' | 'critical'
    platform_health: number
    total_workers: number
    healthy_count: number
    worker_statuses: Array<{
      name: string
      ecosystem: string | null
      classification_status?: 'classified' | 'violation'
      status: string
      latency_ms?: number | null
      version?: string | null
    }>
    classification_violations?: Array<{ name: string; reason: string; classification_status: 'violation' }>
    last_check: string
  }>('/dashboard/health'),

  workers: () => apiFetch<{
    workers: Array<{
      name: string
      ecosystem: string | null
      classification?: 'internal' | 'external' | 'violation'
      classification_source?: string
      classification_status?: 'classified' | 'violation'
      health_url: string | null
      internal: boolean
      status: string
      http?: number
      version?: string | null
      latency_ms?: number | null
      error?: string
    }>
    ecosystems: Record<string, Array<{
      name: string
      ecosystem: string
      classification?: 'internal' | 'external'
      classification_source?: string
      classification_status?: 'classified'
      health_url: string | null
      internal: boolean
      status: string
      http?: number
      version?: string | null
      latency_ms?: number | null
      error?: string
    }>>
    ecosystem_labels: Record<string, string>
    approved_internal_ecosystems: string[]
    classification_violations: Array<{ name: string; reason: string; classification_status: 'violation' }>
    healthy: number
    total: number
    last_check: string
  }>('/dashboard/workers'),

  audit: (limit?: number, filter?: string) => {
    const params = new URLSearchParams()
    if (limit) params.set('limit', String(limit))
    if (filter) params.set('filter', filter)
    const qs = params.toString()
    return apiFetch<{
      success: boolean
      count: number
      events: Array<{
        id: number
        event_type: string
        source_ip: string
        blocked: number
        details: string
        created_at: string
        tenant_id?: string
      }>
    }>(`/dashboard/audit${qs ? '?' + qs : ''}`)
  },

  security: () => apiFetch<{
    success: boolean
    threats_blocked: { today: number; week: number; month: number }
    quarantine_entries: Array<{
      id: number | string
      source: string
      source_type: string
      reason: string
      created_at: string
      status: string
    }>
    token_revocations: number
    lockdown_status: string
  }>('/dashboard/security'),

  tenants: () => apiFetch<{
    success: boolean
    count: number
    tenants: Array<{
      tenant_id: string
      tenant_name: string
      email: string
      status: string
      role: string
      created_at: string
      requests_today: number
      requests_month: number
      pending_approvals: number
    }>
  }>('/dashboard/tenants'),

  incidents: () => apiFetch<{
    success: boolean
    count: number
    incidents: Array<{
      incident_id: string
      title: string
      status: string
      severity: string
      detected_at: string
      details: string
    }>
  }>('/dashboard/incidents'),

  patches: () => apiFetch<{
    success: boolean
    pending: Array<{ request_id: string; action_type: string; status: string; submitted_at: string }>
    recent_deployments: Array<{ request_id: string; action_type: string; status: string; updated_at: string }>
  }>('/dashboard/patches'),

  mcpStats: () => apiFetch<{
    success: boolean
    tool_count: number
    top_tools: Array<{ name: string; count: number }>
    breakdown: { day: Array<{ event_type: string; c: number }>; week: Array<{ event_type: string; c: number }>; month: Array<{ event_type: string; c: number }> }
  }>('/dashboard/mcp-stats'),

  intelligence: () => apiFetch<{
    success: boolean
    providers: Record<string, { available: boolean; model?: string; cost_usd_today?: number; calls_today?: number; cost_usd_total?: number; calls_total?: number }>
    recent_events: number
    ai_events: Array<{ id: number; event_type: string; details: string; created_at: string }>
  }>('/dashboard/intelligence'),

  memory: () => apiFetch<{
    success: boolean
    kv_namespaces: Record<string, { key_count_sample?: number; error?: string }>
    r2_memory: { knowledge_objects?: number; truncated?: boolean; error?: string }
  }>('/dashboard/memory'),

  performance: () => apiFetch<{
    success: boolean
    period: string
    hourly_buckets: Array<{ hour: string; requests: number; errors: number }>
  }>('/dashboard/performance'),

  resources: () => apiFetch<{
    success: boolean
    d1: { database_id: string; name: string }
    kv: { namespace_count: number; namespaces: Array<{ id: string; title: string }> }
    r2: { bucket_count: number; buckets: Array<{ name: string; created: string }> }
  }>('/dashboard/resources'),

  memoryTree: () => apiFetch<{
    success: boolean
    error?: string
    tree?: MemoryTreeNode
    total_objects?: number
    total_folders?: number
    generated_at?: string
  }>('/dashboard/memory/tree'),

  memoryFile: (key: string) => apiFetch<{
    success: boolean
    key: string
    content: string
    size: number
    uploaded: string | null
  }>(`/dashboard/memory/file?key=${encodeURIComponent(key)}`),

  backups: () => apiFetch<{
    success: boolean
    files: Array<{ key: string; name: string; size: number; uploaded: string }>
  }>('/dashboard/backups'),

  approve: (requestId: string, notes?: string) =>
    apiFetch<{ success: boolean; request_id: string; status: string }>('/dashboard/approve', {
      method: 'POST',
      body: JSON.stringify({ request_id: requestId, executed_by: 'dashboard', notes }),
    }),

  reject: (requestId: string, reason?: string) =>
    apiFetch<{ success: boolean; request_id: string; status: string }>('/dashboard/reject', {
      method: 'POST',
      body: JSON.stringify({ request_id: requestId, executed_by: 'dashboard', reason }),
    }),

  chatStream: (messages: Array<{ role: string; content: unknown }>, mode?: string, sessionId?: string, continueConversation?: unknown[], imageAttachments?: Array<{ name: string; data: string; mimeType: string }>, signal?: AbortSignal) => {
    const token = getToken()
    return fetch(`${BASE_URL}/dashboard/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ messages, mode: mode || 'gpt-mini', ...(sessionId ? { session_id: sessionId } : {}), ...(continueConversation ? { continue_conversation: continueConversation } : {}), ...(imageAttachments && imageAttachments.length > 0 ? { image_attachments: imageAttachments } : {}) }),
      signal,
    })
  },

  chatModes: () => apiFetch<{ modes: Array<{ mode: string; provider: string; model: string; label: string; available: boolean }>; default: string }>('/dashboard/chat/modes'),

  session: () => apiFetch<{
    active: boolean
    session?: {
      session_id: string
      session_type: 'single' | 'orchestrate' | 'platform'
      product: string
      started_at: string
      started_by: string
      scope: string[]
      out_of_scope: string[]
      changes_this_session: Array<{ worker: string; change_type: string; commit_sha: string; timestamp: string }>
      pending_items: Array<{ description: string; target_worker: string; queued_at: string }>
      chat_history_ref: string
    }
  }>('/dashboard/session'),

  sessionStart: (params: { session_type: string; workers?: string[] | null; reason?: string; hours: number; totpCode: string }) =>
    apiFetch<{ success: boolean; session_id: string; session_type: string; locked_workers: string[] | null; scope: string[]; out_of_scope: string[]; expires_at: string; workers_by_ecosystem: Record<string, string[]>; message: string }>('/dashboard/session/start', {
      method: 'POST',
      body: JSON.stringify({ ...params, totp_code: params.totpCode }),
    }),

  sessionSwitch: (params: { new_product: string; new_session_type: string; confirmed?: boolean; totpCode: string }) =>
    apiFetch<{ success?: boolean; needs_confirmation?: boolean; pending_changes?: unknown[]; previous_session?: string; message: string }>('/dashboard/session/switch', {
      method: 'POST',
      body: JSON.stringify({ ...params, totp_code: params.totpCode }),
    }),

  sessionEnd: (totpCode: string) =>
    apiFetch<{ success: boolean; session_id: string; duration_minutes: number; changes_made: number }>(`/dashboard/session?totp_code=${encodeURIComponent(totpCode)}`, {
      method: 'DELETE',
    }),

  sessionModifyWorkers: (params: { action: 'add' | 'remove'; worker: string; totpCode: string }) =>
    apiFetch<{ success: boolean; locked_workers: string[]; message: string }>('/dashboard/session/modify-workers', {
      method: 'POST',
      body: JSON.stringify({ action: params.action, worker: params.worker, totp_code: params.totpCode }),
    }),

  r2List: (prefix: string, limit?: number, cursor?: string) => {
    const params = new URLSearchParams({ prefix })
    if (limit) params.set('limit', String(limit))
    if (cursor) params.set('cursor', cursor)
    return apiFetch<{
      success: boolean
      prefix: string
      objects: Array<{ key: string; size: number; uploaded: string }>
      truncated: boolean
      cursor: string | null
    }>(`/dashboard/r2?${params}`)
  },

  r2Read: (key: string) =>
    apiFetch<{
      success: boolean
      key: string
      content_type: 'json' | 'text'
      data: unknown
    }>(`/dashboard/r2/read?key=${encodeURIComponent(key)}`),

  tokenUsage: () => apiFetch<{
    success: boolean
    days: Array<{ date: string; input_tokens: number; output_tokens: number; calls: number; tier2_calls: number; memory_hits_0A: number; memory_hits_0B: number; cache_hits: number }>
    today: { date: string; input_tokens: number; output_tokens: number; calls: number; tier2_calls: number; memory_hits_0A: number; memory_hits_0B: number; cache_hits: number }
    memory_hit_rate: number
    cache_hit_rate: number
    tokens_today: number
  }>('/dashboard/token-usage'),

  chatExport: (messages: Array<{ role: string; content: string }>, format: string, title?: string) =>
    apiFetch<{
      success: boolean
      content: string
      format: string
      title: string
      sharepoint_url?: string
    }>('/dashboard/chat/export', {
      method: 'POST',
      body: JSON.stringify({ messages, format, title }),
    }),

  chatSaveTo: (service: string, content: string, title?: string) =>
    apiFetch<{
      success: boolean
      url?: string
      message?: string
    }>('/dashboard/chat/save-to', {
      method: 'POST',
      body: JSON.stringify({ service, content, title }),
    }),

  chatFeedback: (messageId: string, type: 'positive' | 'negative', reason?: string, comment?: string, sessionId?: string) =>
    apiFetch<{ success: boolean }>('/dashboard/chat/feedback', {
      method: 'POST',
      body: JSON.stringify({ message_id: messageId, type, reason, comment, session_id: sessionId }),
    }),

  syncRegistry: () => apiFetch<{
    success: boolean
    tool_count: number
    previous_count: number
    changed: boolean
  }>('/dashboard/sync-registry', { method: 'POST' }),

  pushVapidPublicKey: () => apiFetch<{ publicKey: string | null }>('/dashboard/push/vapid-public-key'),

  pushSubscribe: (subscription: unknown) => apiFetch<{ success: boolean; count: number }>('/dashboard/push/subscribe', {
    method: 'POST',
    body: JSON.stringify(subscription),
  }),

  pushUnsubscribe: (endpoint: string) => apiFetch<{ success: boolean; count: number }>('/dashboard/push/unsubscribe', {
    method: 'POST',
    body: JSON.stringify({ endpoint }),
  }),

  pushTest: () => apiFetch<{ success: boolean; sent: boolean }>('/dashboard/push/test', { method: 'POST' }),

  getTierOverrideStatus: () => apiFetch<{
    tier2: { active: boolean; expires_at?: string; remaining_seconds?: number }
    tier3: { active: boolean; expires_at?: string; remaining_seconds?: number }
  }>('/dashboard/tier-override'),

  setTierOverride: (tier: 2 | 3, hours: number, totpCode: string) => apiFetch<{ success: boolean; tier: number; active: boolean; expires_at: string; hours: number }>('/dashboard/tier-override/set', {
    method: 'POST',
    body: JSON.stringify({ tier, hours, totp_code: totpCode }),
  }),

  clearTierOverride: (tier: 2 | 3, totpCode: string) => apiFetch<{ success: boolean; tier: number; active: boolean }>('/dashboard/tier-override/clear', {
    method: 'POST',
    body: JSON.stringify({ tier, totp_code: totpCode }),
  }),

  // Tier 2/3 governed approvals — principal-scoped server-side.
  approvals: () => apiFetch<{
    success: boolean
    count: number
    approvals: Array<{
      token: string
      tool: string
      tier: number
      summary: string
      created_at: string
      expires_in: number
      type?: string
      actionable?: boolean
      source_name?: string
      freshness?: number
      requested_by?: string | null
      requested_by_role?: string | null
      approved_by?: string | null
      approved_by_role?: string | null
      approved_at?: string | null
      executed_at?: string | null
      error?: string | null
      status?: string | null
    }>
  }>('/dashboard/approvals'),

  approveAction: (token: string) => apiFetch<{
    success: boolean
    tool: string
    result: unknown
    approved_at: string
    continuation?: string
    continuation_pending?: { id: string; action: string; tier: number; details: string }
  }>(`/dashboard/approvals/${token}/approve`, { method: 'POST' }),

  denyAction: (token: string) => apiFetch<{
    success: boolean
    denied: boolean
    tool: string
  }>(`/dashboard/approvals/${token}/deny`, { method: 'POST' }),

  approvalDetail: (token: string) => apiFetch<{
    success: boolean
    token: string
    tool: string
    tier: number
    params: Record<string, unknown>
    created_at: string
    requested_by?: string | null
    requested_by_role?: string | null
    approved_by?: string | null
    approved_by_role?: string | null
    approved_at?: string | null
    executed_at?: string | null
    error?: string | null
    status?: string | null
  }>(`/dashboard/approvals/${token}/detail`),

  approvalResults: () => apiFetch<{
    success: boolean
    count: number
    results: Array<{
      token: string
      tool: string
      tier: number
      status: 'executed' | 'failed' | 'denied'
      result_json: string | null
      error: string | null
      created_at: string
      approved_at: string | null
      executed_at: string | null
      approved_by: string | null
    }>
  }>('/dashboard/approvals/results'),

  dismissAlert: (alertId: string) => apiFetch<{
    success: boolean
    dismissed: boolean
  }>(`/dashboard/ke-alerts/${alertId}/dismiss`, { method: 'POST' }),

  images: () => apiFetch<{
    success: boolean
    count: number
    images: Array<{
      image_id: string
      prompt: string
      model_used: string
      size: string
      quality: string
      created_at: string
      serve_url: string
    }>
  }>('/dashboard/images'),

  imagesDelete: (imageIds: string[]) =>
    apiFetch<{ success: boolean; deleted: string[]; failed: Array<{ image_id: string; error: string }> }>('/dashboard/images/delete', {
      method: 'POST',
      body: JSON.stringify({ image_ids: imageIds }),
    }),

  library: () => apiFetch<{
    success: boolean
    count: number
    items: Array<{
      item_id: string
      filename: string
      mime_type: string
      size: number
      is_image: boolean
      uploaded_at: string
      serve_url: string
    }>
  }>('/dashboard/library'),

  modelStatus: () => apiFetch<{
    success: boolean
    fast: { model: string; source: string }
    capable: { model: string; source: string }
    registry?: {
      version: string | null
      source: string
      warnings: string[]
      valid: boolean
    }
    roles?: Array<{
      role: 'tool_orchestrator' | 'deep_reasoner' | 'fast_general' | 'vision' | 'verifier' | string
      active: null | {
        role: string
        provider: string
        model_id: string
        lifecycle_state: string
        qualification_state: string
        declared_capabilities?: Record<string, boolean>
        verified_capabilities?: string[]
        request_defaults?: {
          reasoning_effort?: string
          max_completion_tokens?: number
          parallel_tool_calls?: boolean
        }
        escalation_profile?: {
          reasoning_effort?: string
          max_completion_tokens?: number
        } | null
        fallback_priority?: number
      }
      fallbacks?: Array<{
        role: string
        provider: string
        model_id: string
        lifecycle_state: string
        qualification_state: string
        request_defaults?: {
          reasoning_effort?: string
          max_completion_tokens?: number
          parallel_tool_calls?: boolean
        }
        fallback_priority?: number
      }>
      candidate: null | {
        role: string
        provider: string
        model_id: string
        lifecycle_state: string
        qualification_state: string
      }
    }>
  }>('/dashboard/model-status'),

  libraryUpload: (filename: string, mimeType: string, data: string) =>
    apiFetch<{
      success: boolean
      item: { item_id: string; filename: string; mime_type: string; size: number; is_image: boolean; uploaded_at: string; serve_url: string }
    }>('/dashboard/library/upload', {
      method: 'POST',
      body: JSON.stringify({ filename, mimeType, data }),
    }),

  libraryDelete: (itemIds: string[]) =>
    apiFetch<{ success: boolean; deleted: string[]; failed: Array<{ item_id: string; error: string }> }>('/dashboard/library/delete', {
      method: 'POST',
      body: JSON.stringify({ item_ids: itemIds }),
    }),

  imageMeta: (imageId: string) => apiFetch<{
    success: boolean
    image_id: string
    prompt: string
    model_used: string
    size: string
    quality: string
    created_at: string
    r2_path: string
    serve_url: string
  }>(`/dashboard/images/${encodeURIComponent(imageId)}/meta`),

  actionLog: (result?: string, action?: string, search?: string, limit?: number) => {
    const params = new URLSearchParams()
    if (result) params.set('result', result)
    if (action) params.set('action', action)
    if (search) params.set('search', search)
    if (limit) params.set('limit', String(limit))
    const qs = params.toString()
    return apiFetch<{
      success: boolean
      count: number
      entries: Array<{
        action_id: string
        session_id: string | null
        timestamp: string
        user_request: string | null
        action: string
        target: string | null
        result: 'success' | 'failure' | 'cancelled'
        commit_sha: string | null
        reason: string | null
        actor: string
        error_detail: string | null
        attempt: number
        parent_id: string | null
      }>
    }>(`/dashboard/action-log${qs ? '?' + qs : ''}`)
  },

  extensionActivities: (filters:ExtensionActivityFilters={}) => {
    const params=new URLSearchParams()
    for(const [key,value]of Object.entries(filters)) {
      if(value!==undefined&&value!==null&&String(value)!=='')params.set(key,String(value))
    }
    return apiFetch<{
      success:boolean;events:ExtensionActivityEvent[];count:number;
      has_more:boolean;next_before:string|null;next_before_id:string|null;retention_days:number;
      by_outcome:Record<string,number>
    }>('/dashboard/extension-activities'+(params.toString()?'?'+params:''))
  },

  events: (category?: string, worker?: string, eventType?: string, after?: string, before?: string, limit?: number) => {
    const params = new URLSearchParams()
    if (category) params.set('category', category)
    if (worker) params.set('worker', worker)
    if (eventType) params.set('event_type', eventType)
    if (after) params.set('after', after)
    if (before) params.set('before', before)
    if (limit) params.set('limit', String(limit))
    const qs = params.toString()
    return apiFetch<{
      success: boolean
      count: number
      limit: number
      by_category: Record<string, number>
      events: Array<{
        id: string
        event_type: string
        category: string
        severity: string
        source: string
        module: string | null
        worker: string | null
        build_id: string | null
        correlation_id: string | null
        timestamp: string
        payload: string | null
        status: string
        verified: number
      }>
    }>(`/dashboard/events${qs ? '?' + qs : ''}`)
  },

  tasks: (status?: string, limit?: number) => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    if (limit) params.set('limit', String(limit))
    const qs = params.toString()
    return apiFetch<{
      success: boolean
      count: number
      status_filter: string
      tasks: Array<{
        task_id: string
        created_at: string
        created_by: string
        content: string
        status: 'pending' | 'completed'
        completed_at: string | null
        completed_by: string | null
        source: 'user' | 'ops-bridge'
        completion_type: 'manual' | 'executed' | null
      }>
    }>(`/dashboard/tasks${qs ? '?' + qs : ''}`)
  },

  addTask: (content: string) =>
    apiFetch<{ success: boolean; task_id: string; content: string; status: string }>('/dashboard/tasks', {
      method: 'POST',
      body: JSON.stringify({ content }),
    }),

  completeTask: (taskId: string, completionType: 'manual' | 'executed' = 'manual') =>
    apiFetch<{ success: boolean; task_id: string; status: string; completed_at: string; completion_type: 'manual' | 'executed' }>(
      `/dashboard/tasks/${encodeURIComponent(taskId)}/complete`,
      { method: 'PATCH', body: JSON.stringify({ completion_type: completionType }) }
    ),

  reopenTask: (taskId: string) =>
    apiFetch<{ success: boolean; task_id: string; status: string }>(
      `/dashboard/tasks/${encodeURIComponent(taskId)}/reopen`,
      { method: 'POST' }
    ),

  reminders: () =>
    apiFetch<{
      success: boolean
      count: number
      reminders: Array<{
        reminder_id: string
        content: string
        priority: 'high' | 'normal' | 'low'
        recall_at: string | null
        created_at: string
        created_by: string
        status: string
      }>
    }>('/dashboard/reminders'),

  selfAuditSummary: () =>
    apiFetch<{ success: boolean; summary: { auto_fixed: number; awaiting_review: number; critical: number; last_run: string | null } }>('/dashboard/self-audit/summary'),

  selfAuditRun: () =>
    apiFetch<{
      audit_id: string; date: string; checks_run: string[]
      level_1_found: number; level_1_fixed: number; level_2_found: number
      level_3_found: number; level_4_found: number
      issues: Array<{ level: number; check: string; finding: string; status: string }>
      summary: string
    }>('/dashboard/self-audit/run', { method: 'POST' }),

  selfAuditQueue: (status?: string) => {
    const params = new URLSearchParams()
    if (status) params.set('status', status)
    const qs = params.toString()
    return apiFetch<{
      success: boolean; count: number; status_filter: string
      items: Array<{
        issue_id: string; level: number; detected_at: string; check: string
        description: string; impact: string; patch_type: string
        patch_content: string | null; status: string
        actioned_by: string | null; actioned_at: string | null; action: string | null
      }>
    }>(`/dashboard/self-audit/queue${qs ? '?' + qs : ''}`)
  },

  selfAuditApprove: (issueId: string) =>
    apiFetch<{ success: boolean; issue_id: string; status: string; note: string }>('/dashboard/self-audit/approve', {
      method: 'POST',
      body: JSON.stringify({ issue_id: issueId }),
    }),

  selfAuditReject: (issueId: string, reason?: string) =>
    apiFetch<{ success: boolean; issue_id: string; status: string }>('/dashboard/self-audit/reject', {
      method: 'POST',
      body: JSON.stringify({ issue_id: issueId, reason }),
    }),

  selfAuditHistory: () =>
    apiFetch<{
      success: boolean; count: number
      runs: Array<{ audit_id: string; date: string; level_1_fixed: number; level_2_found: number; level_3_found: number; level_4_found: number; checks_run: number }>
    }>('/dashboard/self-audit/history'),

  selfAuditCritical: () =>
    apiFetch<{
      success: boolean; count: number
      alerts: Array<{ issue_id: string; level: number; detected_at: string; check: string; description: string }>
    }>('/dashboard/self-audit/critical'),

  commandCentre: () => apiFetch<{
    success: boolean; timestamp: string
    systems: { routes: Array<{ route_id: string; source: string; destination: string; route_type: string; classification: string; description: string; status: string; last_seen: string | null }>; route_summary: { total: number; active: number; degraded: number; inactive: number } }
    workers: { total: number; healthy: number; degraded: number; down: number; internal: number; list: Array<{ name: string; product: string; status: string }> }
    alerts: Array<{ id: number; event_type: string; source_ip: string; blocked: number; details: string; created_at: string }>
    incidents: Array<{ incident_id: string; title: string; status: string; severity: string; detected_at: string }>
    tenants: { total: number; active: number }
    pending_approvals: number
    topology_nodes?: Array<{ id: string; label: string; type: string; level: number; parent_id: string | null; color: string; status: string; sentinel: number; criticality: string }>
  }>('/dashboard/command-centre'),

  routes: () => apiFetch<{
    success: boolean; count: number
    routes: Array<{ route_id: string; source: string; destination: string; route_type: string; classification: string; description: string; status: string; last_seen: string | null; created_at: string }>
  }>('/dashboard/routes'),

  librarianFindings: (tenantId?: string, status?: string) => {
    const params = new URLSearchParams()
    if (tenantId) params.set('tenant_id', tenantId)
    if (status) params.set('status', status)
    const qs = params.toString()
    return apiFetch<{
      success: boolean; tenant_id: string; count: number
      findings: Array<{ id: string; type: 'DISCOVERY' | 'MISALIGNMENT' | 'UPDATE_NEEDED'; source_path: string | null; memory_path: string | null; description: string; question: string; status: string; source: string; created_at: string; resolved_at?: string; resolution?: string }>
      crawl_history: Array<{ timestamp: string; sources: string[]; finding_count: number; errors: string[] }>
    }>(`/dashboard/librarian/findings${qs ? '?' + qs : ''}`)
  },

  librarianCrawl: (tenantId?: string, sources?: string[]) =>
    apiFetch<{ success: boolean; tenant_id: string; findings_count: number; findings_path: string; errors: string[]; memory_keys_indexed: number; sharepoint_files_found: number }>(
      '/dashboard/librarian/crawl',
      { method: 'POST', body: JSON.stringify({ tenant_id: tenantId || 'dashboard', sources: sources || ['cora-memory'] }) }
    ),

  librarianResolve: (findingId: string, resolution: string, tenantId?: string, updateContent?: string) =>
    apiFetch<{ success: boolean; finding_id: string; resolution: string; note: string }>('/dashboard/librarian/resolve', {
      method: 'POST',
      body: JSON.stringify({ finding_id: findingId, resolution, tenant_id: tenantId || 'dashboard', update_content: updateContent }),
    }),

  toolPermissions: () => apiFetch<{ success: boolean; global_mode: string; tools: Array<{ name: string; permission: string; tier: number; description: string | null }> }>('/dashboard/tools/permissions'),

  toolMode: () => apiFetch<{ success: boolean; mode: string }>('/dashboard/tools/mode'),

  setToolMode: (mode: string) =>
    apiFetch<{ success: boolean; mode: string }>('/dashboard/tools/mode', {
      method: 'PATCH',
      body: JSON.stringify({ mode }),
    }),

  setToolPermission: (toolName: string, permission: string) =>
    apiFetch<{ success: boolean; tool_name: string; permission: string }>('/dashboard/tools/permission', {
      method: 'PATCH',
      body: JSON.stringify({ tool_name: toolName, permission }),
    }),

  toolPending: () => apiFetch<{ success: boolean; pending: Array<{ id: string; tool: string; args_summary: string; created_at: string; status: string }> }>('/dashboard/tools/pending'),

  toolApprove: (approvalId: string) =>
    apiFetch<{ success: boolean; approval_id: string; status: string }>('/dashboard/tools/approve', {
      method: 'POST',
      body: JSON.stringify({ approval_id: approvalId, approved_by: 'dashboard' }),
    }),

  toolReject: (approvalId: string) =>
    apiFetch<{ success: boolean; approval_id: string; status: string }>('/dashboard/tools/reject', {
      method: 'POST',
      body: JSON.stringify({ approval_id: approvalId }),
    }),

  adminTenants: () => apiFetch<{
    count: number
    tenants: Array<{ tenant_id: string; name: string; slug: string; plan: string; status: string; owner_user_id: string; owner_email: string; owner_name: string | null; member_count: number; created_at: string; updated_at: string }>
  }>('/admin/tenants'),

  adminTenantDetail: (tenantId: string) => apiFetch<Record<string, unknown>>(`/admin/tenants/${encodeURIComponent(tenantId)}`),

  adminSuspendTenant: (tenantId: string, reason?: string) =>
    apiFetch<{ tenant_id: string; status: string; reason: string }>(`/admin/tenants/${encodeURIComponent(tenantId)}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ reason: reason || 'admin action' }),
    }),

  adminActivateTenant: (tenantId: string) =>
    apiFetch<{ tenant_id: string; status: string }>(`/admin/tenants/${encodeURIComponent(tenantId)}/activate`, { method: 'POST' }),

  adminChangePlan: (tenantId: string, plan: string) =>
    apiFetch<{ tenant_id: string; plan: string; rate_limits: Record<string, unknown> }>(`/admin/tenants/${encodeURIComponent(tenantId)}/plan`, {
      method: 'POST',
      body: JSON.stringify({ plan }),
    }),

  adminStats: () => apiFetch<{ tenants: Record<string, number>; total_tenants: number; total_users: number; active_api_keys: number; audit_events_24h: number }>('/admin/stats'),

  buildReviews: (verdict?: string, limit?: number) => {
    const params = new URLSearchParams()
    if (verdict && verdict !== 'all') params.set('verdict', verdict)
    if (limit) params.set('limit', String(limit))
    const qs = params.toString()
    return apiFetch<{ success: boolean; count: number; total: number; reviews: Array<{ review_id: string; commit_sha: string; reviewer: string; reviewed_at: string; change_type: string; overall_verdict: string; blocking_issues: string | null }> }>(`/dashboard/build-reviews${qs ? '?' + qs : ''}`)
  },

  buildReviewCompliance: () => apiFetch<{ success: boolean; reviews_last_30_days: number; reviews_last_7_days: number; not_safe_last_7_days: number; safe_with_conditions_last_7_days: number; safe_last_7_days: number; last_review: { reviewed_at: string; overall_verdict: string; reviewer: string; commit_sha: string } | null; gate_active: boolean }>('/dashboard/build-review-compliance'),

  engReview: (status?: string, priority?: string) => {
    const qs = new URLSearchParams()
    if (status && status !== 'all') qs.set('status', status)
    if (priority && priority !== 'all') qs.set('priority', priority)
    const q = qs.toString()
    return apiFetch<{ success: boolean; recommendations: EngReviewRec[]; total: number; open: number; accepted: number; snoozed: number; completed: number }>(`/dashboard/eng-review${q ? '?' + q : ''}`)
  },

  engReviewAction: (karId: string, action: string, notes?: string, snoozeUntil?: string) =>
    apiFetch<{ success: boolean; kar_id: string; action: string; status: string }>(`/dashboard/eng-review/${karId}/action`, {
      method: 'POST',
      body: JSON.stringify({ action, notes, snooze_until: snoozeUntil }),
    }),

  seedEngReview: (recommendations: Partial<EngReviewRec>[]) =>
    apiFetch<{ success: boolean; inserted: number; total: number }>('/dashboard/eng-review', {
      method: 'POST',
      body: JSON.stringify({ recommendations }),
    }),

  opsbridgeFeatures: (params?: { category?: string }) => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    const q = qs.toString()
    return apiFetch<{
      success: boolean
      count: number
      total: number
      total_tools: number
      summary: { by_category: Record<string, number> }
      platform_features: Array<{
        platform_feature_id: string
        name: string
        description: string | null
        category: string
        tool_count: number
        tool_names: string
        tier_summary: string | null
        location: string | null
        source_doc: string
        last_generated: string | null
        status: string
      }>
    }>(`/dashboard/opsbridge-features${q ? '?' + q : ''}`)
  },

  platformFeatures: (params?: { category?: string; criticality?: string; verification_status?: string }) => {
    const qs = new URLSearchParams()
    if (params?.category) qs.set('category', params.category)
    if (params?.criticality) qs.set('criticality', params.criticality)
    if (params?.verification_status) qs.set('verification_status', params.verification_status)
    const q = qs.toString()
    return apiFetch<{
      success: boolean
      count: number
      total: number
      summary: { by_category: Record<string, number>; by_verification: Record<string, number> }
      platform_features: Array<{
        platform_feature_id: string
        name: string
        description: string | null
        category: string
        criticality: string
        status: string
        visibility: string
        verification_status: string
        last_verified: string | null
        rto_minutes: number
        rpo_minutes: number
        updated_at: string
      }>
    }>(`/dashboard/platform-features${q ? '?' + q : ''}`)
  },

  platformFeatureById: (id: string) =>
    apiFetch<{
      success: boolean
      platform_feature: Record<string, unknown>
      history: Array<{ history_id: string; change_type: string; changed_at: string; changed_by: string; reason: string }>
    }>(`/dashboard/platform-features/${encodeURIComponent(id)}`),

  verifyPlatformFeature: (id: string) =>
    apiFetch<{ success: boolean; platform_feature_id: string; verification_status: string; verified_at: string }>(
      `/dashboard/platform-features/${encodeURIComponent(id)}/verify`,
      { method: 'POST' }
    ),

  updatePlatformFeatureStatus: (id: string, status: string, reason?: string) =>
    apiFetch<{ success: boolean; platform_feature_id: string; status: string }>(
      `/dashboard/platform-features/${encodeURIComponent(id)}/status`,
      { method: 'PATCH', body: JSON.stringify({ status, reason }) }
    ),

  // Cloudflare connection (second account)
  cfConnectionStatus: () => apiFetch<{
    connected: boolean
    account_id?: string
    worker_count?: number
    worker_names?: string[]
    error?: string
  }>('/dashboard/cf-connection/status'),

  cfConnectionStore: (params: { account_id: string; api_token: string }) =>
    apiFetch<{ success: boolean; account_id: string; hint: string; stored_at: string }>(
      '/dashboard/cf-connection/store',
      { method: 'POST', body: JSON.stringify(params) }
    ),

  cfConnectionDisconnect: () =>
    apiFetch<{ success: boolean; tenant_id?: string; disconnected_account_id?: string; error?: string }>(
      '/dashboard/cf-connection/disconnect',
      { method: 'POST' }
    ),

  pipelineQueue: (status?: string, stage?: string, limit?: number) => {
    const qs = new URLSearchParams()
    if (status) qs.set('status', status)
    if (stage) qs.set('stage', stage)
    if (limit) qs.set('limit', String(limit))
    const q = qs.toString()
    return apiFetch<{ success: boolean; count: number; items: PipelineItem[] }>(`/dashboard/pipeline${q ? '?' + q : ''}`)
  },

  pipelineItem: (id: string) =>
    apiFetch<{ success: boolean; item: PipelineItem; stages: PipelineStageOutput[] }>(`/dashboard/pipeline/${id}`),

  pipelineApprove: (id: string, note?: string, engine?: string) =>
    apiFetch<{ success: boolean; approved_stage: string; next_stage: string; worker_triggered: boolean }>(
      `/dashboard/pipeline/${id}/approve`,
      { method: 'POST', body: JSON.stringify({ note: note ?? null, engine: engine ?? null }) }
    ),

  pipelineReject: (id: string, message: string) =>
    apiFetch<{ success: boolean; rejected_stage: string; status: string }>(
      `/dashboard/pipeline/${id}/reject`,
      { method: 'POST', body: JSON.stringify({ message }) }
    ),

  browserLive: () =>
    apiFetch<{ active: boolean; session_id?: string; tenant_id?: string; started_at?: number; screenshot?: string; url?: string; title?: string; error?: string }>('/dashboard/browser/live'),

  browserLiveVideo: () =>
    apiFetch<{ active: boolean; tenant_id?: string; started_at?: number; url?: string; title?: string; video_ws_url?: string | null; error?: string }>('/dashboard/browser/live-video'),

  pipelinePause: (id: string, reason: string) =>
    apiFetch<{ success: boolean; paused_stage: string; status: string }>(
      `/dashboard/pipeline/${id}/pause`,
      { method: 'POST', body: JSON.stringify({ reason }) }
    ),

  pipelineMessage: (id: string, message: string) =>
    apiFetch<{ success: boolean; message_id: string }>(
      `/dashboard/pipeline/${id}/message`,
      { method: 'POST', body: JSON.stringify({ message }) }
    ),

  pipelineRerun: (id: string, stage: string, note?: string) =>
    apiFetch<{ success: boolean; triggered: boolean; stage_output_id?: string }>(
      `/dashboard/pipeline/${id}/rerun`,
      { method: 'POST', body: JSON.stringify({ stage, note: note || undefined }) }
    ),

  platformModelGet: () =>
    apiFetch<PlatformModel>('/dashboard/platform-model'),

  platformModelVersions: () =>
    apiFetch<{ versions: { key: string; uploaded: string; size: number }[] }>('/dashboard/platform-model/versions'),

  platformModelRefresh: (changedWorker?: string) =>
    apiFetch<{ success: boolean; model_status: string; coverage?: unknown; generated_at?: string; error?: string }>(
      '/dashboard/platform-model/refresh',
      { method: 'POST', body: JSON.stringify(changedWorker ? { changed_worker: changedWorker } : {}) }
    ),

  pipelineTrigger: (ideaNumber?: number) =>
    apiFetch<{ success: boolean; triggered: boolean; result?: unknown }>(
      '/dashboard/pipeline/trigger',
      { method: 'POST', body: JSON.stringify({ idea_number: ideaNumber ?? null }) }
    ),

  escalationStatus: () => apiFetch<{
    success: boolean
    active: boolean
    escalation: {
      jobId: string
      question: string
      pollCount: number
      elapsedSeconds: number
      output: string
      startedAt: string
    } | null
  }>('/dashboard/escalation/status'),

  // Licensing admin (SEC-022)
  listLicenses: () => apiFetch<{ success: boolean; count: number; licenses: unknown[] }>('/dashboard/admin/licenses'),
  createLicense: (plan: string, entitlements: string[]) => apiFetch<{ success: boolean; license_id: string; license_key: string; note: string }>('/dashboard/admin/licenses/create', { method: 'POST', body: JSON.stringify({ plan, entitlements }) }),
  getLicense: (id: string) => apiFetch<{ success: boolean; license: unknown }>(`/dashboard/admin/licenses/${id}`),
  revokeLicense: (id: string) => apiFetch<unknown>('/dashboard/admin/licenses/revoke', { method: 'POST', body: JSON.stringify({ license_id: id }) }),
  suspendLicense: (id: string) => apiFetch<unknown>('/dashboard/admin/licenses/suspend', { method: 'POST', body: JSON.stringify({ license_id: id }) }),
  reactivateLicense: (id: string) => apiFetch<unknown>('/dashboard/admin/licenses/reactivate', { method: 'POST', body: JSON.stringify({ license_id: id }) }),
  transferLicense: (id: string) => apiFetch<unknown>('/dashboard/admin/licenses/transfer', { method: 'POST', body: JSON.stringify({ license_id: id }) }),

  // Staff admin (AD-057) — canonical Gateway routes.
  // Keep listStaff's public shape as an array because Capability Control and
  // Staff Portal both consume it; unwrap the Gateway's { staff: [] } envelope here.
  listStaff: async () => {
    const result = await apiFetch<{ staff: unknown[] }>('/admin/staff')
    return Array.isArray(result.staff) ? result.staff : []
  },
  getStaffMember: (id: string) => apiFetch<{ user: Record<string, unknown>; profile: Record<string, unknown> | null }>(`/admin/staff/${encodeURIComponent(id)}`),
  inviteTemplates: () => apiFetch<{
    templates: Array<{
      id: string
      name: string
      description: string
      tone: string
      roles: Array<'admin' | 'manager' | 'developer' | 'staff' | 'viewer'>
      accent: string
      surface: string
      eyebrow: string
    }>
    defaults_by_role: Record<'admin' | 'manager' | 'developer' | 'staff' | 'viewer', string>
  }>('/admin/invite/templates'),
  inviteStaff: (payload: {
    email: string
    display_name: string
    first_name?: string
    role: 'admin' | 'manager' | 'developer' | 'staff' | 'viewer'
    job_title?: string
    department?: string
    template_id?: string
    personal_message?: string
  }) => apiFetch<{ success: boolean; user_id: string; email: string; email_sent: boolean; invitation_expires_at: string; template_id: string }>('/admin/invite', { method: 'POST', body: JSON.stringify(payload) }),
  updateStaffProfile: (id: string, profile: Record<string, string>) => apiFetch<{ success: boolean; user_id: string }>(`/admin/staff/${encodeURIComponent(id)}/profile`, { method: 'PUT', body: JSON.stringify(profile) }),
  lockStaff: (id: string) => apiFetch<{ success: boolean; user_id: string; status: string }>(`/admin/staff/${encodeURIComponent(id)}/lock`, { method: 'POST' }),
  unlockStaff: (id: string) => apiFetch<{ success: boolean; user_id: string; status: string }>(`/admin/staff/${encodeURIComponent(id)}/unlock`, { method: 'POST' }),
  revokeStaff: (id: string) => apiFetch<{ success: boolean; user_id: string; status: string; message?: string }>(`/admin/staff/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),
  resendStaffInvitation: (id: string) => apiFetch<{ success: boolean; user_id: string; email_sent: boolean; invitation_expires_at: string; template_id: string; message?: string }>(`/admin/staff/${encodeURIComponent(id)}/resend-invite`, { method: 'POST' }),
  deleteTestStaff: (id: string, confirmEmail: string) => apiFetch<{ success: boolean; user_id: string; deleted: boolean; message?: string }>(`/admin/staff/${encodeURIComponent(id)}/delete-test`, { method: 'POST', body: JSON.stringify({ confirm: 'DELETE_TEST_ACCOUNT', confirm_email: confirmEmail }) }),
  archiveStaff: (id: string, payload: { employment_ended_at: string; departure_reason: 'resignation' | 'dismissal' | 'redundancy' | 'end_of_contract' | 'retirement' | 'transfer' | 'other'; departure_note?: string }) => apiFetch<{ success: boolean; user_id: string; archive_id: string; status: string; retention_review_at: string }>(`/admin/staff/${encodeURIComponent(id)}/archive`, { method: 'POST', body: JSON.stringify(payload) }),
  listFormerStaff: async () => { const result = await apiFetch<{ staff: Record<string, unknown>[] }>('/admin/former-staff'); return Array.isArray(result.staff) ? result.staff : [] },
  getFormerStaffMember: (id: string) => apiFetch<{ archive: Record<string, unknown>; erasure_requests: Record<string, unknown>[]; lifecycle_events: Record<string, unknown>[] }>(`/admin/former-staff/${encodeURIComponent(id)}`),
  getStaffRetentionPolicy: () => apiFetch<{ policies: Record<string, unknown>[] }>('/admin/former-staff/retention-policy'),
  recordStaffErasureRequest: (id: string, payload: { request_channel: 'email' | 'letter' | 'portal' | 'verbal' | 'other'; scope?: string }) => apiFetch<{ success: boolean; request_id: string; status: string; response_due_at: string; legal_hold: boolean }>(`/admin/former-staff/${encodeURIComponent(id)}/erasure-request`, { method: 'POST', body: JSON.stringify(payload) }),
  setStaffLegalHold: (id: string, hold: boolean, reason?: string) => apiFetch<{ success: boolean; user_id: string; legal_hold: boolean }>(`/admin/former-staff/${encodeURIComponent(id)}/legal-hold`, { method: 'POST', body: JSON.stringify({ hold, reason: reason || '' }) }),

  // My Profile (current user)
  getMyProfile: () => apiFetch<{ user: Record<string, string>; profile: Record<string, string> | null }>('/dashboard/profile'),
  updateMyProfile: (data: Record<string, string>) => apiFetch<{ success: boolean; profile_completed: boolean }>('/dashboard/profile', { method: 'PUT', body: JSON.stringify(data) }),
}
